/**
 * Every TanStack Query key in one place.
 *
 * Filter arguments are typed as `object` rather than `Record<string, unknown>`
 * on purpose: a declared interface has no index signature, so it is not
 * assignable to a Record, and every caller would need a cast.
 *
 * Centralising them is what keeps invalidation correct: a mutation can
 * invalidate `queryKeys.measurements.all` and be sure it catches every list and
 * detail query, without any file having to guess the shape of a key it did not
 * write.
 */
export const queryKeys = {
    session: {
        current: ['session', 'current'] as const,
    },

    /**
     * One key, because /v1/catalog is one response.
     *
     * Earlier this had five sub-keys for goals, equipment, activity types and
     * so on — none of which were ever fetched separately, while the whole
     * payload was cached under the "equipment" one. A key that does not
     * describe what it holds is how a cache ends up serving the wrong thing.
     */
    catalog: {
        all: ['catalog'] as const,
    },

    profile: {
        all: ['profile'] as const,
        detail: () => ['profile', 'detail'] as const,
        equipment: () => ['profile', 'equipment'] as const,
        goals: () => ['profile', 'goals'] as const,
        availability: () => ['profile', 'availability'] as const,
        activityPreferences: () => ['profile', 'activity-preferences'] as const,
    },

    measurements: {
        all: ['measurements'] as const,
        list: (filters?: object) => ['measurements', 'list', filters ?? {}] as const,
        trends: (metric: string, period: string) =>
            ['measurements', 'trends', metric, period] as const,
        latest: () => ['measurements', 'latest'] as const,
    },

    exercises: {
        all: ['exercises'] as const,
        list: (filters?: object) => ['exercises', 'list', filters ?? {}] as const,
        detail: (slug: string) => ['exercises', 'detail', slug] as const,
        /**
         * What this user lifted for this exercise before.
         *
         * Its own key, not `detail`: the two hold different shapes for the same
         * slug, and sharing the key would have one query serve the other's data.
         */
        setHistory: (slug: string) => ['exercises', 'set-history', slug] as const,
    },

    workouts: {
        all: ['workouts'] as const,
        list: (filters?: object) => ['workouts', 'list', filters ?? {}] as const,
        /**
         * The resolved plan for one workout, per equipment mode.
         *
         * The mode is part of the key because the two versions of a workout are
         * genuinely different documents — one has the exercises the user cannot
         * do, the other has their replacements. Sharing a key would make
         * toggling the swap show the previous answer until the refetch landed,
         * which is exactly the moment the user is looking for the difference.
         */
        detail: (slug: string, equipment: string, adaptation: string | null = null) =>
            ['workouts', 'detail', slug, equipment, adaptation] as const,
        /**
         * The ways today's session could be different.
         *
         * Under the `workouts` prefix so a change to the catalogue invalidates it,
         * but its own key: computing it means building every variant and ranking
         * the library, and it is asked for when a dialog opens rather than on
         * every read of a workout. See ADR 0028.
         */
        adaptations: (slug: string) => ['workouts', 'adaptations', slug] as const,
        /**
         * The editable definition of one workout.
         *
         * Separate from `detail`, which holds the resolved plan for the same
         * slug. Two shapes under one key is how a cached POST response ended up
         * standing in for a session's exercise list earlier on.
         */
        blueprint: (slug: string) => ['workouts', 'blueprint', slug] as const,
        /**
         * The filter vocabularies the catalogue actually contains.
         *
         * Derived from the catalogue itself because the API exposes no facet
         * endpoint, and held under the `workouts` prefix so a change to the
         * catalogue invalidates the filters along with the list.
         */
        facets: () => ['workouts', 'facets'] as const,
        recommendations: (filters?: object) =>
            ['workouts', 'recommendations', filters ?? {}] as const,
    },

    sessions: {
        all: ['sessions'] as const,
        active: () => ['sessions', 'active'] as const,
        detail: (id: string) => ['sessions', 'detail', id] as const,
        history: (filters?: object) => ['sessions', 'history', filters ?? {}] as const,
    },

    activities: {
        all: ['activities'] as const,
        list: (filters?: object) => ['activities', 'list', filters ?? {}] as const,
    },

    plans: {
        all: ['plans'] as const,
        week: (weekStart: string) => ['plans', 'week', weekStart] as const,
        current: () => ['plans', 'current'] as const,
    },

    programmes: {
        all: ['programmes'] as const,
        list: () => ['programmes', 'list'] as const,
        detail: (id: string) => ['programmes', 'detail', id] as const,
        /**
         * The enrolment, which is a different shape from any published
         * programme. Its own key for the reason `workouts.blueprint` has one:
         * two shapes under one key is how a cached response ends up standing in
         * for something else.
         */
        mine: () => ['programmes', 'mine'] as const,
    },

    progress: {
        all: ['progress'] as const,
        dashboard: (period: string) => ['progress', 'dashboard', period] as const,
        achievements: () => ['progress', 'achievements'] as const,
        /** One month of the training calendar, `YYYY-MM`. */
        calendar: (month: string) => ['progress', 'calendar', month] as const,
    },

    reminders: {
        all: ['reminders'] as const,
        settings: () => ['reminders', 'settings'] as const,
        inbox: () => ['reminders', 'inbox'] as const,
    },

    integrations: {
        all: ['integrations'] as const,
        spotify: () => ['integrations', 'spotify'] as const,
        spotifyPlaylists: () => ['integrations', 'spotify', 'playlists'] as const,
        playlistTracks: (playlistId: string) =>
            ['integrations', 'spotify', 'playlists', playlistId, 'tracks'] as const,
        musicPreferences: () => ['integrations', 'music-preferences'] as const,
    },
} as const;
